import { useCallback, useEffect, useState } from "react";
import actions, { KYDUserWithGuestlist } from "../../../actions";
import { useLoading } from "../../hooks/form";
import { useDialog } from "../../../Common/Dialog";
import { KYDGuestlistDisplayState } from "@common/interfaces/KYDEvent";

type GuestlistAllocation = KYDGuestlistDisplayState;

export const useGuestlist = (
  event_id: string,
  options?: {
    autoFetch?: boolean;
    initialData?: {
      users?: KYDUserWithGuestlist[];
      allocation?: KYDGuestlistDisplayState;
    };
  }
) => {
  const [loading, setLoading] = useLoading<{ allocation: boolean }>({
    allocation: false,
  });
  const [guestlist, setGuestlist] = useState<GuestlistAllocation | null>(
    options?.initialData?.allocation || {
      allocations: [
        {
          allocation: 0,
          remaining: 0,
          assigned: 0,
          ticket_type_id: "",
          is_processing: false,
        },
      ],
      unique_people: 0,
    }
  );
  const [users, setUsers] = useState<KYDUserWithGuestlist[]>(
    options?.initialData?.users || []
  );
  const openMessageDialog = useDialog();
  const autoFetch = options?.autoFetch !== false;

  const updateGuestlistAllocation = useCallback(
    async (allocation: KYDGuestlistDisplayState) => {
      setLoading("allocation", true);
      try {
        const data = await actions.ADMIN.updateGuestlistAllocation({
          event_id: event_id,
          allocation,
        });

        if (data.statusCode !== 200) {
          return Promise.reject(data.message);
        }

        await fetchGuestlist(event_id);

        console.log({ onGuestlistAllocationChange: data });
      } catch (e) {
        openMessageDialog({
          title: "Error",
          text: e.message,
        });
      } finally {
        setLoading("allocation", false);
      }
    },
    [guestlist]
  );

  const fetchGuestlist = useCallback(
    async (event_id: string) => {
      try {
        if (!event_id) return;

        setLoading("allocation", true);
        const data = await actions.ADMIN.fetchGuestlist(event_id);

        if (data.allocation) {
          setGuestlist(data.allocation);
        }

        if (data.users) {
          setUsers(data.users);
        }
      } catch (err) {
        openMessageDialog({
          title: "Error",
          text: err.message,
        });
      } finally {
        setLoading("allocation", false);
      }
    },
    [event_id]
  );

  useEffect(() => {
    if (options?.initialData?.allocation) {
      setGuestlist(options.initialData.allocation);
    }
    if (options?.initialData?.users) {
      setUsers(options.initialData.users);
    }
  }, [options?.initialData?.allocation, options?.initialData?.users]);

  useEffect(() => {
    if (event_id && autoFetch) {
      fetchGuestlist(event_id);
    }
  }, [event_id, autoFetch, fetchGuestlist]);

  return {
    refetch: () => fetchGuestlist(event_id),
    updateGuestlistAllocation,
    guestlist,
    users,
    loading: loading.allocation,
    cache: {
      setGuestlist,
      setUsers,
    },
  };
};
